import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ClientLayout } from "../components/ClientLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Check, X, AlertTriangle, Send, ArrowLeft, RefreshCw, Plus, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const ClientRequest = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  
  const [request, setRequest] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Upload state
  const [uploadingFor, setUploadingFor] = useState(null);
  const [replaceDocId, setReplaceDocId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [docName, setDocName] = useState("");
  const [docPassword, setDocPassword] = useState("");
  const [hasPassword, setHasPassword] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Payment state
  const [processing, setProcessing] = useState(false);
  const [retryingPayment, setRetryingPayment] = useState(false);
  const [cancellingRequest, setCancellingRequest] = useState(false);

  useEffect(() => {
    const init = async () => {
      await markMessagesRead();
      await fetchData();
    };

    init();
  }, [requestId]);


  const scrollMessagesToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollMessagesToBottom();
    }
  }, [messages.length]);

  const markMessagesRead = async () => {
    try {
      await api.put("/messages/mark-read");
    } catch (err) {
      console.error("Failed to mark messages as read", err);
    }
  };

  const fetchData = async () => {
    try {
      const [reqRes, docsRes, msgRes] = await Promise.all([
        api.get(`/requests/${requestId}`),
        api.get(`/requests/${requestId}/documents`),
        api.get("/messages")
      ]);
      setRequest(reqRes.data);
      setDocuments(docsRes.data);
      setMessages(msgRes.data);
    } catch (err) {
      toast.error("Failed to load request details");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const uploadLimits = request?.upload_limits || {
    max_files_per_case: 10,
    max_file_size_mb: 10,
    max_case_storage_mb: 100,
  };

  const uploadedFileCount = documents.length;
  const uploadedBytes = documents.reduce(
    (sum, doc) => sum + (doc.file_size_bytes || 0),
    0
  );

  // Get document for a specific type
  const getDocumentForType = (docType) => {
    return documents.find(d => d.document_type === docType);
  };

  // Get additional documents (not in required list)
  const getAdditionalDocuments = () => {
    if (!request) return [];
    return documents.filter(d => !request.required_documents.includes(d.document_type));
  };

  // Check if all required documents are uploaded and approved
  const allDocsApproved = () => {
    if (!request) return false;
    return request.required_documents.every(docType => {
      const doc = getDocumentForType(docType);
      return doc && doc.status === 'approved';
    });
  };

  // Check if all required documents are uploaded (any status)
  const allDocsUploaded = () => {
    if (!request) return false;
    return request.required_documents.every(docType => {
      const doc = getDocumentForType(docType);
      return doc !== undefined;
    });
  };

  // Check if there are rejected documents that need re-upload
  const hasRejectedDocs = () => {
    return documents.some(d => d.status === 'rejected' || d.status === 'needs_revision');
  };

  // Handle click on required document - opens upload dialog
  const handleDocumentClick = (docType) => {
    const existingDoc = getDocumentForType(docType);
    
    if (existingDoc && (existingDoc.status === 'rejected' || existingDoc.status === 'needs_revision' || existingDoc.status === 'pending')) {
      // Allow replacing pending (not yet reviewed), rejected or needs_revision docs
      setReplaceDocId(existingDoc.id);
    } else if (existingDoc) {
      // Document is approved or under_review — cannot replace without admin permission
      return;
    }
    
    setUploadingFor(docType);
    setDocName(docType);
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0]

    if (file) {
      const isAdditionalDocument = uploadingFor === "Other";
      const existingDoc = !isAdditionalDocument
        ? documents.find(d => d.document_type === uploadingFor)
        : null;
      const isReplacement = !!existingDoc;

      if (file.size > uploadLimits.max_file_size_mb * 1024 * 1024) {
        toast.error(`File size must be less than ${uploadLimits.max_file_size_mb}MB`);
        e.target.value = "";
        return;
      }

      if (!isReplacement && uploadedFileCount >= uploadLimits.max_files_per_case) {
        toast.error(`Maximum ${uploadLimits.max_files_per_case} files allowed for this case`);
        e.target.value = "";
        return;
      }

      const existingSize = isReplacement ? (existingDoc.file_size_bytes || 0) : 0;
      const projectedTotal = uploadedBytes - existingSize + file.size;

      if (projectedTotal > uploadLimits.max_case_storage_mb * 1024 * 1024) {
        const remainingMb = Math.max(
          0,
          (uploadLimits.max_case_storage_mb * 1024 * 1024 - (uploadedBytes - existingSize)) / (1024 * 1024)
        );
        toast.error(`This file exceeds the remaining case storage limit of ${remainingMb.toFixed(2)}MB`);
        e.target.value = "";
        return;
      }

      setUploadFile(file);

      if (!docName) {
        setDocName(file.name.split('.')[0]);
      }
    }

    e.target.value = "";
  };

  // Upload document
  const handleUpload = async () => {
    if (!uploadFile || !uploadingFor || !docName) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);

    try {
      const presignRes = await api.post(`/requests/${requestId}/documents/presign`, {
        document_type: uploadingFor,
        file_name: uploadFile.name,
        file_size_bytes: uploadFile.size,
        content_type: uploadFile.type || "application/octet-stream"
      });

      const { upload_url, storage_key, headers, method } = presignRes.data;

      const uploadRes = await fetch(upload_url, {
        method: method || "PUT",
        headers: headers || {
          "Content-Type": uploadFile.type || "application/octet-stream"
        },
        body: uploadFile
      });

      if (!uploadRes.ok) {
        throw new Error("Direct upload failed");
      }

      await api.post(`/requests/${requestId}/documents/complete`, {
        name: docName,
        document_type: uploadingFor,
        file_name: uploadFile.name,
        file_size_bytes: uploadFile.size,
        storage_key,
        password: hasPassword ? docPassword : null
      });

      toast.success(replaceDocId ? "Document replaced successfully!" : "Document uploaded successfully!");
      resetUploadState();
      await fetchData();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to upload document";

      if (msg.includes("approved")) {
        toast.error("This document is approved. Please message admin to request changes.");
      } else {
        toast.error(msg);
      }
      setUploading(false);
    }
  };

  const resetUploadState = () => {
    setUploadFile(null);
    setUploadingFor(null);
    setReplaceDocId(null);
    setDocName("");
    setDocPassword("");
    setHasPassword(false);
    setUploading(false);
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await api.post("/messages", { content: newMessage });
      toast.success("Message sent!");
      setNewMessage("");
      await fetchData();
      scrollMessagesToBottom();
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };



  const getDocStatus = (docType) => {
    const doc = getDocumentForType(docType);
    if (!doc) return { status: 'missing', label: 'Upload Required', color: 'var(--muted)' };
    
    const statusMap = {
      pending: { label: 'Under Review', color: 'var(--warning)' },
      approved: { label: 'Approved', color: 'var(--success)' },
      rejected: { label: 'Rejected - Reupload', color: 'var(--error)' },
      needs_revision: { label: 'Needs Changes', color: 'var(--warning)' }
    };
    
    return { status: doc.status, ...statusMap[doc.status], doc };
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "Pending";

      case "documents_uploaded":
        return "Documents Uploaded";

      case "under_review":
        return "Under Review";

      case "completed":
        return "Review Completed";

      case "tax_filed":
        return "Tax Filed";

      case "closed":
        return "Closed";

      case "rejected":
        return "Rejected";

      default:
        return status.replace('_', ' ');
    }
  };

  const getProgress = () => {
    if (!request) return 0;
    const total = request.required_documents?.length || 0;
    if (total === 0) return 0;

    const approved = request.required_documents.filter(dt => {
      const doc = getDocumentForType(dt);
      return doc && doc.status === 'approved';
    }).length;

    return Math.round((approved / total) * 100);
  };

  const getProgressMessage = () => {
    // Priority 1: request status (business truth)
    if (request?.status === "tax_filed") return "Tax has been filed. We’ll share the acknowledgement if applicable.";
    if (request?.status === "closed") return "Case closed. Thank you for choosing us";
    if (request?.status === "completed") return "Review completed. Filing will be done next (if not already filed).";
    if (request?.status === "under_review") return "Your documents are under review. We’ll update you if changes are needed.";
    if (request?.status === "documents_uploaded") return "Documents submitted. Review will start shortly.";
    if (request?.status === "rejected") return "There are issues with the case. Please check messages or re-upload required documents.";

    // Priority 2: document situation
    if (hasRejectedDocs()) return "Some documents need changes. Please re-upload the requested documents.";
    if (allDocsApproved()) return "All documents approved. Waiting for the next step.";
    if (allDocsUploaded()) return "Documents uploaded. Waiting for review.";
    return "Upload the documents you have to get started. You can still proceed with payment, and our team may ask for more later.";
  };


  if (loading) {
    return (
      <ClientLayout>
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </ClientLayout>
    );
  }

  const handleRetryPayment = async () => {
    setRetryingPayment(true);
    try {
      const res = await api.post(`/requests/${requestId}/retry-payment`);
      const { payment_url, payment_id, gateway, request: req } = res.data;

      if (gateway === "mock" || !payment_url) {
        toast.success("Payment processed.");
        await fetchData();
        return;
      }

      if (gateway === "razorpay" || payment_url.startsWith("razorpay://")) {
        const parts = payment_url.replace("razorpay://", "").split("|");
        const [rzKeyId, rzOrderId, rzPaymentId] = parts;
        localStorage.setItem("pending_payment_id", rzPaymentId);
        localStorage.setItem("pending_request_id", req.id);

        const options = {
          key: rzKeyId,
          order_id: rzOrderId,
          name: "MoolTax",
          description: `Tax Filing — ${request?.plan_name}`,
          theme: { color: "#1a1a1a" },
          handler: function () { navigate("/payment/callback"); },
          modal: {
            ondismiss: function () {
              toast.error("Payment cancelled. Use 'Retry Payment' to try again.");
              setRetryingPayment(false);
            }
          }
        };

        const openCheckout = () => { const rzp = new window.Razorpay(options); rzp.open(); };
        if (window.Razorpay) { openCheckout(); }
        else {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = openCheckout;
          document.body.appendChild(script);
        }
        return;
      }

      // PhonePe
      localStorage.setItem("pending_payment_id", payment_id);
      localStorage.setItem("pending_request_id", req.id);
      window.location.href = payment_url;

    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setRetryingPayment(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!window.confirm("Are you sure you want to cancel this request? This cannot be undone.")) return;
    setCancellingRequest(true);
    try {
      await api.delete(`/requests/${requestId}/cancel`);
      toast.success("Request cancelled. You can now select a new plan.");
      navigate("/plans");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel this request.");
    } finally {
      setCancellingRequest(false);
    }
  };

  if (!request) return null;

  // Payment wall — show retry/cancel UI when payment is pending
  if (request.payment_status === "unpaid") {
    return (
      <ClientLayout>
        <div className="max-w-xl mx-auto py-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-sm opacity-60 hover:opacity-100 mb-6"
          >
            <ArrowLeft size={16} /> Back to dashboard
          </button>

          <div
            className="bg-white rounded-2xl border p-8 text-center"
            style={{ borderColor: "var(--border)" }}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 text-2xl"
              style={{ backgroundColor: "#fff3e0" }}
            >
              ₹
            </div>
            <h1
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Payment required
            </h1>
            <p className="opacity-70 mb-1">
              {request.plan_name} — FY {request.financial_year}
            </p>
            <p
              className="text-3xl font-bold mb-2"
              style={{ color: "var(--accent)" }}
            >
              ₹{request.price?.toLocaleString()}
            </p>
            <p className="text-sm opacity-60 mb-8">
              Complete payment to start uploading documents and activate your case.
            </p>

            <button
              onClick={handleRetryPayment}
              disabled={retryingPayment || cancellingRequest}
              className="btn-primary w-full py-3 rounded-lg text-base mb-3 disabled:opacity-50"
            >
              {retryingPayment ? "Opening payment..." : "Complete Payment"}
            </button>

            <button
              onClick={handleCancelRequest}
              disabled={cancellingRequest || retryingPayment}
              className="w-full py-3 rounded-lg border text-sm opacity-70 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              {cancellingRequest ? "Cancelling..." : "Wrong plan? Cancel & choose again"}
            </button>

            <p className="text-xs opacity-40 mt-4">
              Cancelling will delete this request. No payment has been taken.
            </p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-6">
        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        />

        {/* Back Button & Header */}
        <div>
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4" data-testid="back-btn">
            <ArrowLeft size={20} className="mr-2" /> Back to Dashboard
          </Button>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>{request.plan_name}</h1>
              <p className="opacity-70">
                FY {request.financial_year} • {request.plan_type === 'salary' ? 'Salary' : 'Business'}
                {request.tax_regime ? ` • ${request.tax_regime === 'not_sure' ? 'Not Sure' : `${request.tax_regime.charAt(0).toUpperCase() + request.tax_regime.slice(1)} Regime`}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`status-${request.status}`}>
                {getStatusLabel(request.status)}
              </Badge>

              <Badge className={`status-${request.payment_status}`}>{request.payment_status}</Badge>
            </div>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Document Status</h2>
            <span
              className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ backgroundColor: 'var(--secondary)', color: 'var(--accent)' }}
            >
              {documents.filter(d => d.status === 'approved').length} approved
              {documents.filter(d => d.status === 'under_review').length > 0 &&
                ` · ${documents.filter(d => d.status === 'under_review').length} under review`}
            </span>
          </div>
          <p className="text-sm opacity-60">
            Upload what you have — your CA will guide you on anything else needed via messages.
          </p>
        </div>

        {/* Required Documents - Click to Upload */}
        <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Fraunces, serif' }}>Recommended Documents</h2>
          {['under_review', 'completed', 'tax_filed', 'closed'].includes(request.status) ? (
            <div className="flex items-start gap-2 p-3 rounded-lg mb-4 text-sm"
              style={{ backgroundColor: '#fff3e0', color: '#e65100' }}>
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <span>
                {request.status === 'under_review'
                  ? 'Your case is under review. Documents are locked. Your CA will message you if anything else is needed.'
                  : request.status === 'completed'
                  ? 'Your case review is complete. Document uploads are closed.'
                  : request.status === 'tax_filed'
                  ? 'Your ITR has been filed. Document uploads are closed.'
                  : 'This case is closed. Document uploads are no longer accepted.'}
              </span>
            </div>
          ) : (
            <p className="text-sm opacity-60 mb-4">
              Upload the documents you currently have. If anything else is needed, our tax expert will request it later.
            </p>
          )}
          
          <div className="grid gap-4">
            {request.required_documents.map((docType, idx) => {
              const docStatus = getDocStatus(docType);
              // Case statuses where uploads are completely blocked
              const caseBlocked = ['under_review', 'completed', 'tax_filed', 'closed'].includes(request.status);
              // When case is blocked, only allow if admin explicitly requested it
              const adminRequestedChange = docStatus.status === 'rejected' || docStatus.status === 'needs_revision';
              // When case is open (pending / documents_uploaded / rejected), allow pending and missing too
              const caseOpen = ['pending', 'documents_uploaded', 'rejected'].includes(request.status);
              const isClickable = (!caseBlocked && (docStatus.status === 'missing' || docStatus.status === 'pending'))
                || adminRequestedChange;
              
              return (
                <div
                  key={idx}
                  onClick={() => isClickable && handleDocumentClick(docType)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isClickable ? 'cursor-pointer hover:shadow-md hover:border-orange-300' : 'cursor-default'
                  }`}
                  style={{ 
                    borderColor: docStatus.status === 'missing' ? 'var(--border)' : docStatus.color,
                    backgroundColor: docStatus.status === 'approved' ? '#f0fdf4' : 
                                    docStatus.status === 'rejected' || docStatus.status === 'needs_revision' ? '#fef2f2' : 
                                    'white'
                  }}
                  data-testid={`doc-slot-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        docStatus.status === 'missing' ? 'border-2 border-dashed' : ''
                      }`} style={{ 
                        borderColor: docStatus.status === 'missing' ? 'var(--border)' : 'transparent',
                        backgroundColor: docStatus.status === 'missing' ? 'transparent' : `${docStatus.color}20`
                      }}>
                        {docStatus.status === 'missing' ? (
                          <Upload size={20} className="opacity-40" />
                        ) : docStatus.status === 'approved' ? (
                          <Check size={24} style={{ color: docStatus.color }} />
                        ) : docStatus.status === 'rejected' || docStatus.status === 'needs_revision' ? (
                          <RefreshCw size={20} style={{ color: docStatus.color }} />
                        ) : docStatus.status === 'pending' ? (
                          <RefreshCw size={20} style={{ color: 'var(--warning)' }} />
                        ) : (
                          <FileText size={20} style={{ color: docStatus.color }} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{docType}</p>
                        <p className="text-sm" style={{ color: docStatus.color }}>{docStatus.label}</p>
                        {docStatus.doc?.admin_notes && (
                          <p className="text-sm mt-1 text-red-600">
                            <AlertTriangle size={12} className="inline mr-1" />
                            {docStatus.doc.admin_notes}
                          </p>
                        )}
                      </div>
                    </div>
                    {isClickable && (
                      <Button variant="outline" size="sm" className="shrink-0">
                        {docStatus.status === 'missing' ? 'Upload' : 'Replace'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Document Upload */}
          <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
            <p className="text-sm opacity-70">
              {uploadedFileCount} / {uploadLimits.max_files_per_case} files used •
              {(uploadedBytes / (1024 * 1024)).toFixed(2)} / {uploadLimits.max_case_storage_mb} MB used
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setUploadingFor("Other");
                setDocName("");
                fileInputRef.current?.click();
              }}
              className="w-full"
              data-testid="upload-additional-btn"
            >
              <Plus size={18} className="mr-2" /> Upload Additional Document
            </Button>
          </div>

          {/* Additional Documents List */}
          {getAdditionalDocuments().length > 0 && (
            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileText size={18} /> Additional Documents Uploaded
              </h3>
              <div className="space-y-3">
                {getAdditionalDocuments().map((doc) => (
                  <div 
                    key={doc.id}
                    className="p-3 rounded-lg border flex items-center justify-between"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${getDocStatus(doc.document_type).color}20` }}
                      >
                        <FileText size={18} style={{ color: getDocStatus(doc.document_type).color }} />
                      </div>
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-sm" style={{ color: getDocStatus(doc.document_type).color }}>
                          {doc.status.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline">Additional</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>



        {/* What Happens Next */}
        {request.payment_status === 'paid' && (
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
            <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              What happens next?
            </h2>

            <div className="space-y-0">
              {[
                {
                  step: 1,
                  label: "Payment confirmed",
                  desc: "Your payment is received and your case is now active.",
                  done: true,
                  active: false,
                },
                {
                  step: 2,
                  label: "Upload your documents",
                  desc: "Upload whatever documents you have. If anything else is needed, your CA will ask via messages.",
                  done: ["documents_uploaded","under_review","completed","tax_filed","closed"].includes(request.status),
                  active: request.status === "pending",
                },
                {
                  step: 3,
                  label: "Expert review",
                  desc: "Your assigned CA will review your documents within 1–3 business days and reach out if more information is needed.",
                  done: ["completed","tax_filed","closed"].includes(request.status),
                  active: request.status === "under_review" || request.status === "documents_uploaded",
                },
                {
                  step: 4,
                  label: "Tax return filed",
                  desc: "Your CA prepares and files your ITR on the Income Tax portal. You will receive the acknowledgement (ITR-V).",
                  done: ["tax_filed","closed"].includes(request.status),
                  active: request.status === "completed",
                },
                {
                  step: 5,
                  label: "All done",
                  desc: "Your tax filing is complete. Keep the ITR-V acknowledgement for your records.",
                  done: request.status === "closed",
                  active: request.status === "tax_filed",
                },
              ].map((item, idx, arr) => (
                <div key={idx} className="flex gap-4">
                  {/* Connector line + circle */}
                  <div className="flex flex-col items-center">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold"
                      style={{
                        backgroundColor: item.done
                          ? '#dcfce7'
                          : item.active
                          ? 'var(--primary)'
                          : 'var(--secondary)',
                        color: item.done
                          ? '#16a34a'
                          : item.active
                          ? 'white'
                          : 'var(--foreground)',
                        opacity: item.done || item.active ? 1 : 0.4,
                      }}
                    >
                      {item.done ? <Check size={14} /> : item.step}
                    </div>
                    {idx < arr.length - 1 && (
                      <div
                        className="w-0.5 flex-grow my-1"
                        style={{
                          backgroundColor: item.done ? '#bbf7d0' : 'var(--border)',
                          minHeight: '20px',
                        }}
                      />
                    )}
                  </div>

                  {/* Content */}
                  <div className="pb-6 flex-1">
                    <p
                      className="font-semibold text-sm mb-1"
                      style={{
                        color: item.active
                          ? 'var(--primary)'
                          : item.done
                          ? '#16a34a'
                          : 'var(--foreground)',
                        opacity: item.done || item.active ? 1 : 0.45,
                      }}
                    >
                      {item.label}
                      {item.active && (
                        <span
                          className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: 'var(--secondary)', color: 'var(--accent)' }}
                        >
                          You are here
                        </span>
                      )}
                    </p>
                    <p
                      className="text-sm leading-relaxed"
                      style={{
                        color: 'var(--foreground)',
                        opacity: item.done || item.active ? 0.7 : 0.35,
                      }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Current status callout */}
            <div
              className="mt-2 p-4 rounded-lg text-sm"
              style={{ backgroundColor: 'var(--secondary)' }}
            >
              <p className="font-medium mb-0.5" style={{ color: 'var(--primary)' }}>Current status</p>
              <p style={{ opacity: 0.75 }}>{getProgressMessage()}</p>
            </div>
          </div>
        )}

        {/* Messages Section */}
        <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Fraunces, serif' }}>Messages</h2>
          
          <div ref={messagesContainerRef} className="space-y-4 max-h-80 overflow-y-auto mb-6">
            {messages.length === 0 ? (
              <p className="text-center opacity-60 py-8">No messages yet. Send a message to get started.</p>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`max-w-[80%] p-4 rounded-lg ${msg.sender_type === 'client' ? 'ml-auto' : ''}`}
                  style={{ 
                    backgroundColor: msg.sender_type === 'client' ? 'var(--primary)' : 'var(--secondary)',
                    color: msg.sender_type === 'client' ? 'white' : 'inherit'
                  }}
                  data-testid={`message-${msg.id}`}
                >
                  <p className="text-sm">{msg.content}</p>
                  <p className="text-xs opacity-60 mt-1">
                    {msg.sender_name} • {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="flex gap-3">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1"
              data-testid="message-input"
            />
            <Button
              onClick={handleSendMessage}
              disabled={sendingMessage || !newMessage.trim()}
              className="btn-primary"
              data-testid="send-message-btn"
            >
              <Send size={20} />
            </Button>
          </div>
        </div>
      </div>

      {/* Upload Confirmation Dialog */}
      <Dialog open={!!uploadFile} onOpenChange={() => resetUploadState()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>
              {replaceDocId ? 'Replace Document' : 'Upload Document'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--secondary)' }}>
              <p className="font-medium">{uploadFile?.name}</p>
              <p className="text-sm opacity-60">For: {uploadingFor}</p>
              {replaceDocId && (
                <p className="text-sm text-orange-600 mt-1">
                  This will replace the existing document.
                  {documents.find(d => d.id === replaceDocId)?.status === 'pending'
                    ? " The previous file will be removed as it hasn't been reviewed yet."
                    : " Upload the corrected document as requested."}
                </p>
              )}
            </div>
            <div>
              <Label>Document Name</Label>
              <Input
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                className="mt-2"
                placeholder="Enter document name"
                data-testid="doc-name-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasPassword"
                checked={hasPassword}
                onCheckedChange={setHasPassword}
              />
              <label htmlFor="hasPassword" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                <Lock size={14} /> This document has a password
              </label>
            </div>
            {hasPassword && (
              <div>
                <Label>Document Password</Label>
                <Input
                  type="text"
                  value={docPassword}
                  onChange={(e) => setDocPassword(e.target.value)}
                  className="mt-2"
                  placeholder="Enter password to open document"
                  data-testid="doc-password-input"
                />
                <p className="text-xs opacity-60 mt-1">This will help our team open your document</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetUploadState}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading} className="btn-primary" data-testid="confirm-upload-btn">
              {uploading ? <span className="spinner" style={{ width: 20, height: 20 }}></span> : (replaceDocId ? "Replace" : "Upload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </ClientLayout>
  );
};

export default ClientRequest;